// B-131 — "Mapa de oficinas" in the Dashboard: where the chosen client's offices are and
// how they reconcile, on an interactive map of Colombia (Leaflet over Esri's Light Gray canvas — no
// key, attribution shown; CARTO's light map now asks for one).
//
// An office is placed only from what the registry declares (`utils/ubicacionOficinas.js`: its DANE
// code, or its declared city) — never guessed from its name. Offices in one municipality share one
// point, coloured by how many of their closings reconcile; a click shows its figures and offices.
// The summary and the legend float on the map; beside it, the municipalities worst first, each
// with its health as a bar, which fly the map to the point. The offices that cannot be placed are
// said under the map, by name, not hidden.
//
// B-136 — it is also the page's filter: a point (or a municipality in the list) chooses its offices,
// an office in a point's card chooses that office alone, and `onSeleccion` hands the choice to the
// page, whose charts and tables then read only those offices. Choosing again, or the page's
// "Ver todas las oficinas", hands back null.
import { useEffect, useMemo, useRef, useState } from "react";
import { CircleMarker, MapContainer, Popup, TileLayer, Tooltip, ZoomControl, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./styles/mapaOficinas.css";
import { MapPin, MapPinOff } from "lucide-react";
import { cn } from "@mantis-sas/ui";

import { moneda } from "../../../utils/cierrePanel";
import { ubicarOficina } from "../../../utils/ubicacionOficinas";

const COLOMBIA = [4.6, -74.1];
// The view stays on Colombia (and its islands): nothing of the client is anywhere else.
const LIMITES = [[-5.5, -83], [14.5, -65.5]];
const ESRI = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas";

// How a group of offices reconciles, as a colour: the share of their closings that balance.
export const tonoDeSalud = (salud) => {
  if (salud === null) return { color: "#94a3b8", etiqueta: "Sin cierres" };
  if (salud >= 90) return { color: "#10b981", etiqueta: "Cuadra (≥ 90%)" };
  if (salud >= 60) return { color: "#f59e0b", etiqueta: "Con novedades (60–89%)" };
  return { color: "#e11d48", etiqueta: "En riesgo (< 60%)" };
};

const LEYENDA = [tonoDeSalud(95), tonoDeSalud(70), tonoDeSalud(10), tonoDeSalud(null)];

// The municipality chosen in the list or on the map: a colour none of the health tones uses, so it
// stands out whatever its health — which its popup and its row still say.
export const SELECCIONADO = { color: "#2563eb", etiqueta: "Seleccionado" };

/**
 * The map's points: one per municipality holding at least one office, with its offices and their
 * closings summed; plus the offices that could not be placed.
 *
 * @param {Array} oficinas  `listCierreOffices`' offices (officeCode, name, dane, city, departamento, region)
 * @param {Map} resumen     office code -> { total, cuadre, discrepancia }
 */
export function puntosDelMapa(oficinas, resumen) {
  const porMunicipio = new Map();
  const sinUbicacion = [];
  oficinas.forEach((oficina) => {
    const lugar = ubicarOficina(oficina);
    const datos = resumen.get(oficina.officeCode) || { total: 0, cuadre: 0, discrepancia: 0 };
    const fila = { codigo: oficina.officeCode, nombre: oficina.name, ...datos };
    if (!lugar) {
      sinUbicacion.push(fila);
      return;
    }
    const punto = porMunicipio.get(lugar.dane) || { ...lugar, oficinas: [], total: 0, cuadre: 0, discrepancia: 0 };
    punto.oficinas.push(fila);
    punto.total += datos.total;
    punto.cuadre += datos.cuadre;
    punto.discrepancia += datos.discrepancia;
    porMunicipio.set(lugar.dane, punto);
  });
  const puntos = [...porMunicipio.values()].map((p) => ({
    ...p,
    salud: p.total ? Math.round((p.cuadre / p.total) * 100) : null,
  }));
  return { puntos, sinUbicacion };
}

/** Fits the view to the points once they are known, and flies to the one chosen in the list. */
function Encuadre({ puntos, foco }) {
  const map = useMap();
  useEffect(() => {
    if (!puntos.length) return;
    // The card may have been laid out after the map measured itself: measure again, or the
    // fit is computed for a smaller box and Colombia comes out a corner of the continent.
    map.invalidateSize();
    if (puntos.length === 1) {
      map.setView([puntos[0].lat, puntos[0].lng], 9);
      return;
    }
    map.fitBounds(puntos.map((p) => [p.lat, p.lng]), { paddingTopLeft: [24, 80], paddingBottomRight: [24, 56], maxZoom: 9 });
  }, [map, puntos]);
  useEffect(() => {
    // A point clicked on the map is already in view: only a choice in the list flies there.
    if (foco && foco.vuela) map.flyTo([foco.lat, foco.lng], Math.max(map.getZoom(), 9), { duration: 0.7 });
  }, [map, foco]);
  return null;
}

const pct = (n, d) => (d ? Math.round((n / d) * 100) : null);

// `calculando`: the offices are placed but their closings are still loading — the points are drawn
// grey and the figures wait, rather than the whole map.
export function MapaOficinas({ oficinas, resumen, cliente, cargando, calculando = false, seleccion = null, onSeleccion = null }) {
  const [foco, setFoco] = useState(null);
  // The page cleared the choice ("Ver todas las oficinas"): the map lets go of its point too.
  useEffect(() => {
    if (!seleccion) setFoco(null);
  }, [seleccion]);
  const { puntos, sinUbicacion } = useMemo(() => puntosDelMapa(oficinas || [], resumen), [oficinas, resumen]);
  const ranking = useMemo(
    () => [...puntos].sort((a, b) => (a.salud ?? 101) - (b.salud ?? 101) || Math.abs(b.discrepancia) - Math.abs(a.discrepancia)),
    [puntos]
  );
  const totales = useMemo(() => {
    const oficinasEnMapa = puntos.reduce((n, p) => n + p.oficinas.length, 0);
    const total = puntos.reduce((n, p) => n + p.total, 0);
    const cuadre = puntos.reduce((n, p) => n + p.cuadre, 0);
    return { oficinasEnMapa, salud: pct(cuadre, total), enRiesgo: puntos.filter((p) => p.salud !== null && p.salud < 60).length };
  }, [puntos]);
  const maxOficinas = Math.max(1, ...puntos.map((p) => p.oficinas.length));
  const elegidoDane = foco ? foco.dane : null;
  // The chosen point is drawn last, so no neighbour covers it.
  const enOrden = useMemo(
    () => [...puntos].sort((a, b) => (a.dane === elegidoDane) - (b.dane === elegidoDane)),
    [puntos, elegidoDane]
  );
  const filas = useRef({});
  const elegir = (p, vuela) => {
    setFoco({ dane: p.dane, lat: p.lat, lng: p.lng, vuela, t: Date.now() });
    if (!onSeleccion) return;
    // The point already chosen, chosen again, lets go: every office again.
    if (seleccion && seleccion.tipo === "municipio" && seleccion.dane === p.dane) {
      onSeleccion(null);
      return;
    }
    onSeleccion({ tipo: "municipio", dane: p.dane, etiqueta: p.municipio, codigos: p.oficinas.map((o) => o.codigo) });
  };
  const elegirOficina = (p, o) => {
    setFoco({ dane: p.dane, lat: p.lat, lng: p.lng, vuela: false, t: Date.now() });
    if (onSeleccion) onSeleccion({ tipo: "oficina", dane: p.dane, etiqueta: o.nombre, codigos: [o.codigo] });
  };
  // A point clicked on the map brings its row into view in the list.
  useEffect(() => {
    if (foco && !foco.vuela && filas.current[foco.dane]) filas.current[foco.dane].scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [foco]);

  return (
    <section className="overflow-hidden rounded-[14px] border bg-card shadow-brinks-card">
      <header className="flex items-start gap-3 border-b px-4 py-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-brinks-surface text-brinks-foreground">
          <MapPin className="h-4 w-4" />
        </span>
        <div>
          <h2 className="text-[14px] font-semibold text-brinks-foreground">Mapa de oficinas</h2>
          <p className="mt-0.5 text-[12px] text-brinks-muted-foreground">
            Dónde están las oficinas{cliente ? ` de ${cliente}` : ""} y cómo cuadran sus cierres en el periodo.
            {onSeleccion ? " Toca un punto o una oficina para ver solo sus datos." : ""}
          </p>
        </div>
      </header>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="mapa-oficinas relative h-[500px] bg-[#e9ebee]">
          {cargando ? (
            <div className="grid h-full place-items-center text-[12.5px] text-brinks-muted-foreground">Cargando el mapa…</div>
          ) : (
            <>
              <MapContainer
                center={COLOMBIA}
                zoom={5}
                zoomSnap={0.25}
                minZoom={5}
                maxBounds={LIMITES}
                maxBoundsViscosity={0.8}
                scrollWheelZoom={false}
                zoomControl={false}
                className="h-full w-full"
              >
                <TileLayer
                  attribution="Tiles &copy; Esri — Esri, HERE, Garmin, &copy; OpenStreetMap"
                  url={`${ESRI}/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}`}
                  maxZoom={16}
                />
                <TileLayer url={`${ESRI}/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}`} maxZoom={16} opacity={0.7} />
                <ZoomControl position="bottomright" />
                <Encuadre puntos={puntos} foco={foco} />
                {enOrden.map((p) => {
                  const tono = tonoDeSalud(p.salud);
                  const elegido = p.dane === elegidoDane;
                  const color = elegido ? SELECCIONADO.color : tono.color;
                  // 6px for one office, up to 11 for the municipality holding the most; the chosen one a size up.
                  const radio = 6 + Math.round(Math.sqrt(p.oficinas.length / maxOficinas) * 5 * (maxOficinas > 1 ? 1 : 0)) + (elegido ? 3 : 0);
                  return (
                    <span key={p.dane}>
                      <CircleMarker
                        // Remounted when chosen, so the halo takes (or drops) its pulse.
                        key={elegido ? "halo-elegido" : "halo"}
                        center={[p.lat, p.lng]}
                        radius={radio + (elegido ? 10 : 7)}
                        interactive={false}
                        pathOptions={{ stroke: false, fillColor: color, fillOpacity: elegido ? 0.22 : 0.16, className: elegido ? "mapa-punto-latido" : "" }}
                      />
                      <CircleMarker
                        key={elegido ? "punto-elegido" : "punto"}
                        center={[p.lat, p.lng]}
                        radius={radio}
                        pathOptions={{ color: "#ffffff", weight: elegido ? 3 : 2, fillColor: color, fillOpacity: 1 }}
                        eventHandlers={{ click: () => elegir(p, false) }}
                      >
                        <Tooltip direction="top" offset={[0, -radio]} permanent={elegido}>
                          <span className="text-[12px] font-semibold">{p.municipio}</span>
                          <span className="text-[11px] text-brinks-muted-foreground">
                            {" "}· {p.salud === null ? "sin cierres" : `${p.salud}% cuadra`}
                          </span>
                        </Tooltip>
                        <Popup>
                          <div className="min-w-[230px] font-redhat">
                            <p className="m-0 text-[13.5px] font-semibold text-brinks-foreground">{p.municipio}</p>
                            <p className="m-0 text-[11.5px] text-brinks-muted-foreground">{p.departamento}</p>
                            <div className="mt-2.5 grid grid-cols-3 gap-2 rounded-[10px] bg-brinks-surface px-2 py-2 text-center">
                              <div>
                                <p className="m-0 text-[15px] font-semibold" style={{ color: tono.color }}>{p.salud === null ? "—" : `${p.salud}%`}</p>
                                <p className="m-0 text-[10.5px] text-brinks-muted-foreground">cuadra</p>
                              </div>
                              <div>
                                <p className="m-0 text-[15px] font-semibold text-brinks-foreground">{p.total}</p>
                                <p className="m-0 text-[10.5px] text-brinks-muted-foreground">cierres</p>
                              </div>
                              <div>
                                <p className="m-0 text-[12.5px] font-semibold text-brinks-foreground">{moneda(p.discrepancia)}</p>
                                <p className="m-0 text-[10.5px] text-brinks-muted-foreground">discrepancia</p>
                              </div>
                            </div>
                            {onSeleccion ? <p className="m-0 mt-2 text-[10.5px] text-brinks-muted-foreground">Toca una oficina para ver solo esa.</p> : null}
                            <ul className="m-0 mt-1 max-h-36 list-none overflow-y-auto p-0">
                              {p.oficinas.map((o) => {
                                const sola = seleccion && seleccion.tipo === "oficina" && seleccion.codigos[0] === o.codigo;
                                return (
                                  <li key={o.codigo} className="border-b last:border-0">
                                    <button
                                      type="button"
                                      disabled={!onSeleccion}
                                      aria-pressed={Boolean(sola)}
                                      title={onSeleccion ? "Ver solo esta oficina" : undefined}
                                      onClick={() => elegirOficina(p, o)}
                                      className={cn(
                                        "flex w-full justify-between gap-3 rounded-[6px] px-1 py-1 text-left text-[11.5px] transition enabled:hover:bg-brinks-surface-hover",
                                        sola && "bg-[#2563eb]/[0.08] font-medium"
                                      )}
                                    >
                                      <span className="truncate">{o.codigo} · {o.nombre}</span>
                                      <span className="whitespace-nowrap text-brinks-muted-foreground">
                                        {o.total ? `${pct(o.cuadre, o.total)}%` : "—"}
                                      </span>
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </Popup>
                      </CircleMarker>
                    </span>
                  );
                })}
              </MapContainer>

              {/* The summary and the legend float on the map, over a frosted card. */}
              <div className="pointer-events-none absolute left-3 top-3 z-[500] flex gap-px overflow-hidden rounded-[12px] border border-white/60 bg-white/75 shadow-lg backdrop-blur">
                {[
                  ["Oficinas", totales.oficinasEnMapa],
                  ["Municipios", puntos.length],
                  ["Cuadra", calculando ? "…" : totales.salud === null ? "—" : `${totales.salud}%`],
                  ["En riesgo", calculando ? "…" : totales.enRiesgo],
                ].map(([etiqueta, valor]) => (
                  <div key={etiqueta} className="px-3.5 py-2">
                    <p className="m-0 text-[10.5px] font-medium uppercase tracking-wide text-brinks-muted-foreground">{etiqueta}</p>
                    <p className="m-0 font-lora text-[18px] font-semibold leading-tight text-brinks-foreground">{valor}</p>
                  </div>
                ))}
              </div>
              {calculando ? (
                <div className="pointer-events-none absolute right-3 top-3 z-[500] flex items-center gap-2 rounded-full border border-white/60 bg-white/80 px-3 py-1.5 text-[11.5px] text-brinks-foreground shadow-lg backdrop-blur">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-primary" />
                  Calculando el cuadre de cada municipio…
                </div>
              ) : null}
              <ul
                className="pointer-events-none absolute bottom-3 left-3 z-[500] m-0 flex list-none flex-wrap gap-x-3 gap-y-1 rounded-[10px] border border-white/60 bg-white/75 px-3 py-2 shadow-lg backdrop-blur"
                aria-label="Leyenda"
              >
                {[...LEYENDA, ...(foco ? [SELECCIONADO] : [])].map((t) => (
                  <li key={t.etiqueta} className="inline-flex items-center gap-1.5 text-[11px] text-brinks-foreground">
                    <span className="h-2.5 w-2.5 rounded-full ring-2 ring-white" style={{ backgroundColor: t.color }} />
                    {t.etiqueta}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <aside className="flex h-[500px] flex-col border-t lg:border-l lg:border-t-0">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <p className="m-0 text-[12.5px] font-semibold text-brinks-foreground">Municipios por atender</p>
            <span className="rounded-full bg-brinks-surface px-2 py-0.5 text-[11px] tabular-nums text-brinks-muted-foreground">
              {ranking.length}
            </span>
          </div>
          <ul className="m-0 flex-1 list-none overflow-y-auto p-0">
            {ranking.map((p) => {
              const tono = tonoDeSalud(p.salud);
              const elegido = foco && foco.dane === p.dane;
              return (
                <li key={p.dane} ref={(el) => { filas.current[p.dane] = el; }}>
                  <button
                    type="button"
                    aria-pressed={Boolean(elegido)}
                    onClick={() => elegir(p, true)}
                    className={cn(
                      "w-full border-b border-l-[3px] border-l-transparent px-4 py-2.5 text-left transition hover:bg-brinks-surface-hover",
                      elegido && "border-l-[#2563eb] bg-[#2563eb]/[0.06]"
                    )}
                  >
                    <span className="flex items-center justify-between gap-3">
                      <span className="min-w-0">
                        <span className="block truncate text-[12.5px] font-medium text-brinks-foreground">{p.municipio}</span>
                        <span className="block truncate text-[11px] text-brinks-muted-foreground">
                          {p.oficinas.length} {p.oficinas.length === 1 ? "oficina" : "oficinas"} · {p.total} cierres
                        </span>
                      </span>
                      <span className="text-right">
                        <span className="block text-[13px] font-semibold tabular-nums" style={{ color: tono.color }}>
                          {p.salud === null ? "—" : `${p.salud}%`}
                        </span>
                        <span className={cn("block text-[10.5px] tabular-nums", p.discrepancia ? "text-brinks-danger" : "text-brinks-muted-foreground")}>
                          {moneda(p.discrepancia)}
                        </span>
                      </span>
                    </span>
                    <span className="mt-1.5 block h-1 w-full overflow-hidden rounded-full bg-brinks-surface">
                      <span className="block h-full rounded-full" style={{ width: `${p.salud ?? 0}%`, backgroundColor: tono.color }} />
                    </span>
                  </button>
                </li>
              );
            })}
            {!ranking.length && !cargando ? (
              <li className="px-4 py-8 text-center text-[12px] text-brinks-muted-foreground">
                Ninguna oficina de este cliente tiene municipio en el registro.
              </li>
            ) : null}
          </ul>
        </aside>
      </div>

      {sinUbicacion.length ? (
        <footer className="flex items-start gap-2 border-t bg-brinks-surface px-4 py-2.5 text-[11.5px] text-brinks-muted-foreground">
          <MapPinOff className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <span className="font-semibold text-brinks-foreground">{sinUbicacion.length} sin ubicación</span>
            {" — el registro no dice su municipio: "}
            {sinUbicacion.slice(0, 8).map((o) => o.nombre).join(", ")}
            {sinUbicacion.length > 8 ? ` y ${sinUbicacion.length - 8} más` : ""}.
          </span>
        </footer>
      ) : null}
    </section>
  );
}

export default MapaOficinas;
